/**
 * Platformer editor schema.
 *
 * Mirrors the shape of trainsim.schema.js so the same generic
 * StageEditorScene can edit either game without code changes.
 * The schema declares:
 *
 *   - `grid`            — board geometry (cellSize / cols / rows).
 *   - `requiredLayers`  — gameplay layers the runtime depends on
 *                         (`terrain`, `entities`).
 *   - `defaultLayers`   — layer set inserted on a fresh stage,
 *                         including decoration layers above + below
 *                         the gameplay layers for parallax.
 *   - `tilePalette`     — every tile type the palette can paint, with
 *                         per-tile `kind`, footprint, allowed layers,
 *                         and SkinResolver slot.
 *   - `entityTypes`     — placeable entity types (player.spawn,
 *                         goomba, koopa, coin, mushroom, flag.goal,
 *                         checkpoint), their props, and `max` limits.
 *   - `renderer`        — adapter pointing at the dual-use
 *                         PlatformerBoard with editor mode on.
 *
 * No `trackHelpers` is declared because the platformer doesn't have
 * a graph-based routing system; the editor's deep-validation skips
 * the track passes and falls through to its generic structure check
 * (max-1 enforcement, layer membership, on-grid bounds).
 */

import { PlatformerBoard } from '../../games/platformer/PlatformerBoard.js';
import {
    PLATFORMER_GRID,
    PLATFORMER_PHYSICS,
    PLATFORMER_TILE_DEFS,
    PLATFORMER_ENTITY_DEFS,
    PLATFORMER_LAYERS,
} from '../../games/platformer/platformerConfig.js';
import {
    DEFAULT_COLLISION_LAYER_NAMES,
    defaultCollisionFor,
} from '../../games/platformer/PlatformerCollision.js';
import {
    FX_EMITTER_PRESETS,
    CAMERA_ZONE_INFO,
    LIGHT_INFO,
    PLAYER_INTERACT_INFO,
    NOTE_INFO,
} from './platformer.schema.content.js';

// Re-export so any downstream tool that grabs presets/help straight from
// the schema module (without knowing about the split) keeps working.
export {
    FX_EMITTER_PRESETS,
    CAMERA_ZONE_INFO,
    LIGHT_INFO,
    PLAYER_INTERACT_INFO,
    NOTE_INFO,
};

/**
 * Build the tilePalette array from `PLATFORMER_TILE_DEFS`. Each entry
 * matches the shape the StageEditorScene expects (`type`, `label`,
 * `group`, `kind: 'sprite'`, `slot`, `footprint`, allowed layers).
 *
 * Tiles fall into two buckets:
 *   - `kind: 'tile'` in the config → solid gameplay tile, allowed
 *     only on the `terrain` layer so the editor can't accidentally
 *     drop a brick on the foreground.
 *   - `kind: 'decor'` in the config → decoration; allowed on any
 *     decoration layer (sky, foreground).
 */
function buildTilePalette() {
    const items = [];
    for (const [type, def] of Object.entries(PLATFORMER_TILE_DEFS)) {
        const isGameplay = def.kind === 'tile';
        const group = pickGroup(type, isGameplay);
        items.push({
            type,
            label: prettyLabel(type),
            group,
            kind: 'sprite',
            slot: def.slot,
            // Autotile-enabled terrain shows an AUTO badge in the
            // palette — paint a blob and edges pick themselves.
            ...(def.autotile ? { autotile: true } : {}),
            // Slopes flip their facing with R (rot is stored per tile).
            ...(def.rotates ? { rotates: true } : {}),
            footprint: def.footprint || { w: 1, h: 1 },
            // Bricks / blocks / pipes only land on `terrain`. Decor
            // tiles drop on any decoration layer (sky, foreground).
            ...(isGameplay
                ? { allowedLayerIds: [PLATFORMER_LAYERS.terrain] }
                : { allowedLayerKinds: ['decoration'] }),
        });
    }
    return items;
}

/**
 * Tile-type → palette group string, used for the editor's left-hand
 * group headers ("Ground", "Pipes", "Decor"). Keeping the mapping
 * here (rather than another field on the tile def) means the runtime
 * can stay schema-free.
 */
function pickGroup(type, isGameplay) {
    if (type.startsWith('slope.')) return 'Slopes';
    if (type.startsWith('pipe.'))  return 'Pipes';
    if (type === 'pole.flag')      return 'Goal';
    if (type === 'castle')         return 'Decor';
    if (type.startsWith('hill.'))  return 'Decor';
    if (type.startsWith('cloud.')) return 'Decor';
    if (type.startsWith('bush.'))  return 'Decor';
    if (type.startsWith('block.')) return 'Blocks';
    return isGameplay ? 'Ground' : 'Decor';
}

/** Convert a tile-type id like 'block.question' into 'Block: Question'. */
function prettyLabel(type) {
    const parts = type.split('.');
    if (parts.length === 1) return cap(parts[0]);
    return `${cap(parts[0])}: ${parts.slice(1).map(cap).join(' ')}`;
}
function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

export const PLATFORMER_SCHEMA = {
    game: 'platformer',

    grid: {
        cols: PLATFORMER_GRID.cols,
        rows: PLATFORMER_GRID.rows,
        cellSize: PLATFORMER_GRID.cellSize,
    },

    /**
     * Platformer levels scroll horizontally, so the editor lets the
     * designer grow / shrink the stage WIDTH in whole-screen chunks
     * (the runtime camera already pans across stages wider than one
     * screen). `min`/`max` are expressed in columns; the editor's
     * Canvas toolbar group only appears when `width` is true.
     *
     * Rows stay fixed at the schema height (the playfield is exactly
     * one screen tall) — only width is user-resizable here.
     */
    allowResize: {
        width: true,
        minCols: PLATFORMER_GRID.cols,        // never shrink below the starter ~2 screens
        maxCols: PLATFORMER_GRID.cols * 8,     // up to ~16 screens of level
    },

    /**
     * Background layer is fully author-controlled per stage. The image
     * itself comes from the `platformer.background` SkinResolver slot
     * (importable via the Content Browser); these settings control how
     * that image is laid out and how it scrolls:
     *
     *   - parallax — scroll factor vs the camera (0 = pinned still,
     *                <1 = drifts slower = depth, 1 = locked to world).
     *   - offsetX / offsetY — pixel nudge of the image.
     *   - scale    — multiplies the fitted size (zoom the art).
     *   - fit      — cover / contain / stretch within the board rect.
     *   - opacity  — 0..1 fade (e.g. wash the backdrop behind the level).
     *
     * `defaults` seed a fresh stage; the editor shows a Background panel
     * (top of the Layers rail) whenever `editable` is true.
     */
    background: {
        editable: true,
        slot: 'platformer.background',
        fitOptions: ['cover', 'contain', 'stretch'],
        defaults: { parallax: 0.3, offsetX: 0, offsetY: 0, scale: 1, fit: 'cover', opacity: 1 },

        /**
         * Phase 4 — stacked parallax background layers. The editor shows
         * a "Parallax Layers" manager under the base backdrop; each layer
         * draws an importable image (`slots` pool) at its own depth +
         * auto-scroll. `defaults` seed a freshly-added layer.
         *   - parallaxX — horizontal scroll factor vs camera
         *                 (0 = pinned/deepest, 1 = locked to world).
         *   - scrollX   — continuous auto-scroll speed (px/sec; clouds).
         *   - scale     — zoom the art (1 = fill stage height).
         *   - offsetY   — vertical nudge (px).
         *   - opacity   — 0..1 fade.
         */
        layers: {
            slots: [
                'platformer.bg.1', 'platformer.bg.2', 'platformer.bg.3',
                'platformer.bg.4', 'platformer.bg.5', 'platformer.bg.6',
            ],
            defaults: { parallaxX: 0.4, parallaxY: 0, scrollX: 0, scrollY: 0, opacity: 1, scale: 1, offsetY: 0 },
        },
    },

    /**
     * Phase 5 — stage-wide physics tuning. The editor renders a Physics
     * panel (inspector, no-selection state) bound to `stage.physics`;
     * the runtime merges those values UNDER the player.spawn's own props
     * (so a per-spawn tweak still wins) and OVER the shared
     * PLATFORMER_PHYSICS defaults. Every field's `default` mirrors the
     * engine constant so the panel shows the true baseline. Groups are
     * purely cosmetic (panel section headers).
     */
    physics: {
        editable: true,
        groups: [
            {
                label: 'Gravity & Falling',
                fields: [
                    { key: 'gravity', label: 'Gravity', min: 0.1, max: 2, step: 0.01, default: PLATFORMER_PHYSICS.gravity,
                      hint: 'Downward acceleration per frame. Higher = heavier.' },
                    { key: 'fallGravityMult', label: 'Fall Gravity ×', min: 1, max: 3, step: 0.05, default: PLATFORMER_PHYSICS.fallGravityMult,
                      hint: 'Extra gravity while falling. >1 = snappy fast-fall (Celeste/Mario feel).' },
                    { key: 'terminalVelocity', label: 'Max Fall Speed', min: 4, max: 40, step: 1, default: PLATFORMER_PHYSICS.terminalVelocity,
                      hint: 'Velocity cap while falling (px/frame).' },
                ],
            },
            {
                label: 'Run & Movement',
                fields: [
                    { key: 'walkSpeed', label: 'Walk Speed', min: 1, max: 12, step: 0.1, default: PLATFORMER_PHYSICS.walkSpeed,
                      hint: 'Top horizontal speed when walking (px/frame).' },
                    { key: 'runSpeed', label: 'Run Speed', min: 1, max: 16, step: 0.1, default: PLATFORMER_PHYSICS.runSpeed,
                      hint: 'Top speed while the run key is held.' },
                    { key: 'friction', label: 'Friction', min: 0, max: 1, step: 0.01, default: PLATFORMER_PHYSICS.friction,
                      hint: 'Ground deceleration when not pressing left/right. Lower = slidey.' },
                    { key: 'airControl', label: 'Air Control', min: 0, max: 1, step: 0.05, default: PLATFORMER_PHYSICS.airControl,
                      hint: 'Mid-air steering authority (fraction of ground accel).' },
                ],
            },
            {
                label: 'Jump',
                fields: [
                    { key: 'jumpStrength', label: 'Jump Strength', min: PLATFORMER_PHYSICS.jumpStrengthMin, max: PLATFORMER_PHYSICS.jumpStrengthMax, step: 0.5, default: Math.abs(PLATFORMER_PHYSICS.jumpVelocity),
                      hint: 'Launch velocity (magnitude). Higher = bigger leap.' },
                    { key: 'jumpReleaseVelocity', label: 'Short-Hop Cap', min: -16, max: 0, step: 0.5, default: PLATFORMER_PHYSICS.jumpReleaseVelocity,
                      hint: 'vy clamp when the jump button is released early (closer to 0 = shorter tap-hop).' },
                    { key: 'coyoteFrames', label: 'Coyote Frames', min: 0, max: 20, step: 1, default: PLATFORMER_PHYSICS.coyoteFrames, int: true,
                      hint: 'Frames you can still jump after leaving a ledge.' },
                    { key: 'jumpBufferFrames', label: 'Jump Buffer', min: 0, max: 20, step: 1, default: PLATFORMER_PHYSICS.jumpBufferFrames, int: true,
                      hint: 'Frames an early jump press is remembered before landing.' },
                ],
            },
        ],
    },

    /**
     * Stage-wide Camera defaults. Rendered in the inspector's no-selection
     * state (under Physics); bound to `stage.camera`. `zoom` is the base
     * follow zoom for the whole level (1 = classic framing, >1 = zoomed in
     * on the player, <1 = pulled back). `smooth` is how quickly the camera
     * eases toward the player. Camera Zone entities override these locally
     * for scripted moments. A value equal to the default clears the override
     * so the stage JSON only carries deliberate tweaks.
     */
    camera: {
        editable: true,
        fields: [
            { key: 'zoom', label: 'Default Zoom', min: 0.5, max: 3, step: 0.05, default: 1,
              hint: 'Base zoom while following the player. 1 = normal, >1 zooms in on the player, <1 pulls back for a wider view. Camera Zones can override this in specific spots.' },
            { key: 'smooth', label: 'Follow Smoothing', min: 1, max: 20, step: 0.5, default: 8,
              hint: 'How quickly the camera catches up to the player. Lower = lazy, floaty follow; higher = tight, locked-on.' },
        ],
    },

    /**
     * Stage-wide Lighting. Rendered in the inspector's no-selection state
     * (under Camera); bound to `stage.lighting`. When `enabled` is off the
     * whole lightmap pass is skipped (zero cost — the classic full-bright
     * look). When on, the scene is dimmed to the ambient floor and Point
     * Light entities carve brightness back in. The directional light is a
     * global "sun/moon" wash whose angle the gradient follows.
     */
    lighting: {
        editable: true,
        fields: [
            { key: 'enabled', kind: 'choice', options: ['off', 'on'],
              optionLabels: ['Off', 'On'], default: 'off', label: 'Lighting',
              hint: 'Master switch. Off = no lighting pass (full bright, zero cost). On = dim the world to the ambient level and let Point Lights illuminate it.',
              info: LIGHT_INFO },
            { key: 'ambient', kind: 'number', min: 0, max: 1, step: 0.02, default: 0.35, label: 'Ambient Brightness',
              hint: 'How lit the UNLIT world is. 0 = pitch black (only lights show); higher lifts everything toward the Ambient Colour. ~0.3–0.5 = moody night. For near-daylight, set Ambient Colour to white and this near 1.' },
            { key: 'ambientColor', kind: 'color', default: '#1a2236', label: 'Ambient Colour',
              hint: 'Colour the unlit world is multiplied with. Dark blue = night, brown = cave, near-black = dungeon, white = neutral (no tint).' },
            { key: 'dir', kind: 'number', min: 0, max: 1, step: 0.02, default: 0, label: 'Directional (Sun)',
              hint: '0 = off. Otherwise a global wash that lifts the whole scene, brighter on the side the light comes from.' },
            { key: 'dirColor', kind: 'color', default: '#fff2cc', label: 'Sun Colour',
              hint: 'Colour of the directional light. Warm = daylight, pale blue = moonlight.' },
            { key: 'dirAngle', kind: 'number', min: 0, max: 360, step: 5, default: 45, label: 'Sun Angle (°)',
              hint: 'Compass angle the directional light comes FROM: 0 = top, 90 = right, 180 = bottom, 270 = left.' },
        ],
    },

    requiredLayers: [
        { id: PLATFORMER_LAYERS.terrain,  defaultLabel: 'Terrain',  order: 0 },
        { id: PLATFORMER_LAYERS.entities, defaultLabel: 'Entities', order: 1 },
    ],

    /**
     * Layer-system capabilities the editor's redesigned Layers panel
     * reads to decide which per-layer controls to surface. Only the
     * platformer opts into the richer model (opacity / solidity /
     * parallax-on-any-layer); the trainsim schema omits `layerCaps`,
     * so its panel keeps the lean track + decoration controls.
     *
     *   - opacity          per-layer alpha slider (0..1)
     *   - collides         per-layer "solid" toggle — tiles on a layer
     *                      flagged solid contribute to platform
     *                      collision (foundation for the Phase-2
     *                      Godot-style collision layers/masks).
     *
     * Parallax stays restricted to decoration (non-gameplay, non-solid)
     * layers in both schemas so the editor preview and the runtime-
     * spawned entities never drift apart.
     */
    layerCaps: {
        opacity: true,
        collides: true,
        // Phase 2: a solid layer can be promoted to a ONE-WAY platform
        // surface (land on top, jump up through, walk past sideways).
        // The Layers panel renders a Solid / One-way / Pass control.
        oneWay: true,
    },

    /**
     * Phase-2 Godot-style collision model. `collisionModel: true` turns
     * on the per-entity Collision section in the inspector (shape +
     * collision layer + scan mask). `collisionLayers` names the 8
     * bitmask layers; a stage may override the names in
     * `stage.collision.layers`. The bit for layer index `i` is `1 << i`.
     */
    collisionModel: true,
    collisionLayers: [...DEFAULT_COLLISION_LAYER_NAMES],

    // `parallax` seeds the per-layer scroll factor on a fresh stage:
    // the sky drifts at half-speed (distant background) while terrain,
    // entities and the foreground lock to the world. Bump the
    // foreground above 1 for elements that should rush past the
    // player. All factors are editable per-layer + per-item.
    //
    // `collides` flags which layers form the solid platform world. The
    // runtime (PlatformerBoard) unions every solid layer into its
    // collision grid, so a designer can keep solid blocks on more than
    // one layer. `opacity` (default 1) fades a whole layer.
    defaultLayers: [
        { id: PLATFORMER_LAYERS.sky,        label: 'Sky',        kind: 'decoration', parallax: 0.5, opacity: 1, collides: false },
        { id: PLATFORMER_LAYERS.terrain,    label: 'Terrain',    kind: 'gameplay',   parallax: 1,   opacity: 1, collides: true  },
        { id: PLATFORMER_LAYERS.entities,   label: 'Entities',   kind: 'gameplay',   parallax: 1,   opacity: 1, collides: false },
        { id: PLATFORMER_LAYERS.foreground, label: 'Foreground', kind: 'decoration', parallax: 1.0, opacity: 1, collides: false },
    ],

    tilePalette: buildTilePalette(),

    /**
     * Extra importable slots the Content Browser should list beyond
     * the tile / entity / background slots it derives automatically.
     * The autotile templates are 4×4 sheets (one per terrain material)
     * whose 16 cells map to the 4-bit neighbour mask — painting that
     * material then picks edges / corners / inner fill automatically.
     */
    extraAssetSlots: [
        { group: 'Tilesets', key: 'platformer.autotile.ground', label: 'Autotile: Ground (4\u00d74)' },
        { group: 'Tilesets', key: 'platformer.autotile.ice',    label: 'Autotile: Ice (4\u00d74)' },
        { group: 'Tilesets', key: 'platformer.autotile.sand',   label: 'Autotile: Sand (4\u00d74)' },
    ],

    entityTypes: [
        {
            type: 'player.spawn',
            label: PLATFORMER_ENTITY_DEFS['player.spawn'].label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            max: 1,
            slot: PLATFORMER_ENTITY_DEFS['player.spawn'].slot,
            collisionDefault: defaultCollisionFor('player.spawn'),
            // Selecting the player spawn opens these in the inspector.
            // Every numeric field overrides the matching global default
            // for THIS level only — leave one untouched and the runtime
            // falls back to the game's standard physics. Tunes the full
            // "feel" of the player: jump, gravity, speed, inertia, etc.
            props: [
                {
                    key: 'startPower',
                    kind: 'choice',
                    options: ['small', 'super'],
                    optionLabels: ['Small', 'Super'],
                    label: 'Starting Power',
                },
                {
                    key: 'jumpStrength', kind: 'number',
                    min: PLATFORMER_PHYSICS.jumpStrengthMin,
                    max: PLATFORMER_PHYSICS.jumpStrengthMax,
                    step: 0.5, default: Math.abs(PLATFORMER_PHYSICS.jumpVelocity),
                    label: 'Jump Height',
                },
                {
                    key: 'gravity', kind: 'number',
                    min: 0.2, max: 1.5, step: 0.05,
                    default: PLATFORMER_PHYSICS.gravity,
                    label: 'Gravity',
                },
                {
                    key: 'walkSpeed', kind: 'number',
                    min: 1, max: 10, step: 0.2,
                    default: PLATFORMER_PHYSICS.walkSpeed,
                    label: 'Walk Speed',
                },
                {
                    key: 'runSpeed', kind: 'number',
                    min: 2, max: 14, step: 0.2,
                    default: PLATFORMER_PHYSICS.runSpeed,
                    label: 'Run Speed',
                },
                {
                    key: 'friction', kind: 'number',
                    min: 0, max: 1, step: 0.02,
                    default: PLATFORMER_PHYSICS.friction,
                    label: 'Ground Friction (Inertia)',
                },
                {
                    key: 'airControl', kind: 'number',
                    min: 0.1, max: 1, step: 0.05,
                    default: PLATFORMER_PHYSICS.airControl,
                    label: 'Air Control',
                },
                {
                    key: 'terminalVelocity', kind: 'number',
                    min: 6, max: 30, step: 1,
                    default: PLATFORMER_PHYSICS.terminalVelocity,
                    label: 'Max Fall Speed',
                },
                {
                    key: 'jumpReleaseVelocity', kind: 'number',
                    min: -10, max: -1, step: 0.5,
                    default: PLATFORMER_PHYSICS.jumpReleaseVelocity,
                    label: 'Short-Hop Cutoff',
                },
                {
                    key: 'coyoteFrames', kind: 'number',
                    min: 0, max: 15, step: 1,
                    default: PLATFORMER_PHYSICS.coyoteFrames,
                    label: 'Coyote Time (frames)',
                },
                {
                    key: 'jumpBufferFrames', kind: 'number',
                    min: 0, max: 15, step: 1,
                    default: PLATFORMER_PHYSICS.jumpBufferFrames,
                    label: 'Jump Buffer (frames)',
                },
            ],
        },
        {
            type: 'goomba',
            label: PLATFORMER_ENTITY_DEFS.goomba.label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS.goomba.slot,
            collisionDefault: defaultCollisionFor('goomba'),
            props: [
                { key: 'dir', kind: 'choice',
                  options: ['left', 'right'],
                  optionLabels: ['Left', 'Right'],
                  label: 'Walk Direction' },
            ],
        },
        {
            type: 'koopa',
            label: PLATFORMER_ENTITY_DEFS.koopa.label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS.koopa.slot,
            collisionDefault: defaultCollisionFor('koopa'),
            props: [
                { key: 'dir', kind: 'choice',
                  options: ['left', 'right'],
                  optionLabels: ['Left', 'Right'],
                  label: 'Walk Direction' },
                { key: 'variant', kind: 'choice',
                  options: ['green', 'red'],
                  optionLabels: ['Green', 'Red'],
                  label: 'Variant' },
            ],
        },
        {
            type: 'coin',
            label: PLATFORMER_ENTITY_DEFS.coin.label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS.coin.slot,
            collisionDefault: defaultCollisionFor('coin'),
            props: [],
        },
        {
            type: 'mushroom',
            label: PLATFORMER_ENTITY_DEFS.mushroom.label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS.mushroom.slot,
            collisionDefault: defaultCollisionFor('mushroom'),
            props: [
                { key: 'kind', kind: 'choice',
                  options: ['super', '1up'],
                  optionLabels: ['Super (Grow)', '1-Up (Extra Life)'],
                  label: 'Mushroom Type' },
            ],
        },
        {
            type: 'flag.goal',
            label: PLATFORMER_ENTITY_DEFS['flag.goal'].label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            max: 1,
            slot: PLATFORMER_ENTITY_DEFS['flag.goal'].slot,
            collisionDefault: defaultCollisionFor('flag.goal'),
            props: [],
        },
        {
            type: 'checkpoint',
            label: PLATFORMER_ENTITY_DEFS.checkpoint.label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            max: 3,
            slot: PLATFORMER_ENTITY_DEFS.checkpoint.slot,
            collisionDefault: defaultCollisionFor('checkpoint'),
            props: [],
        },
        {
            // Moving platform — a kinematic one-way ride. Travels from
            // its spawn cell to the dx/dy offset and back (ping-pong)
            // or wraps (loop), carrying any body standing on top. Lands
            // a falling player on its top edge only — never blocks
            // sideways or from below. The editor draws the slab + a
            // dashed travel path with a ghost at the far endpoint.
            type: 'platform.moving',
            label: PLATFORMER_ENTITY_DEFS['platform.moving'].label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS['platform.moving'].slot,
            collisionDefault: defaultCollisionFor('platform.moving'),
            // The platform is a kinematic surface, not a masked body —
            // landing is handled inside the physics, so the generic
            // Godot layer/mask matrix is irrelevant here.
            noBodyCollision: true,
            props: [
                { key: 'w', kind: 'number', min: 1, max: 12, step: 1, default: 3,
                  label: 'Width (cells)',
                  hint: 'How many tiles wide the platform slab is.' },
                { key: 'dx', kind: 'number', min: -40, max: 40, step: 1, default: 4,
                  label: 'Travel X (cells)',
                  hint: 'Horizontal distance to the far endpoint, in cells. Positive = right, negative = left, 0 = no horizontal motion.' },
                { key: 'dy', kind: 'number', min: -20, max: 20, step: 1, default: 0,
                  label: 'Travel Y (cells)',
                  hint: 'Vertical distance to the far endpoint, in cells. Positive = down, negative = up, 0 = no vertical motion. Combine with Travel X for a diagonal sweep.' },
                { key: 'speed', kind: 'number', min: 0.2, max: 6, step: 0.1, default: 1.2,
                  label: 'Speed (px/frame)',
                  hint: 'How fast the platform travels. 1.2 ≈ a gentle ride; 3+ = a fast lift the player has to time.' },
                { key: 'mode', kind: 'choice',
                  options: ['pingpong', 'loop'],
                  optionLabels: ['Ping-pong (back & forth)', 'Loop (snap back to start)'],
                  default: 'pingpong', label: 'Path Mode',
                  hint: 'Ping-pong = travel to the endpoint, pause, and return the same way (the classic lift). Loop = on reaching the endpoint, jump back to the start and repeat (conveyor-style cycle).' },
                { key: 'pauseSec', kind: 'number', min: 0, max: 5, step: 0.1, default: 0.5,
                  label: 'Pause at Ends (sec)',
                  hint: 'How long the platform waits at each endpoint before setting off again. 0 = never stops.' },
            ],
        },
        {
            // Area2D-style trigger zone. `areaTool: true` opts this type
            // into the editor's rect-drag draw tool — drag a rectangle
            // on the canvas to size the zone directly (a plain click
            // drops the default size, then w/h are editable in the
            // inspector).
            type: 'area.trigger',
            label: PLATFORMER_ENTITY_DEFS['area.trigger'].label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS['area.trigger'].slot,
            collisionDefault: defaultCollisionFor('area.trigger'),
            areaTool: true,
            props: [
                { key: 'effect', kind: 'choice',
                  options: ['hurt', 'coin', 'grow', 'win', 'sound', 'music', 'none'],
                  optionLabels: ['Hurt (damage)', 'Coin (score)', 'Grow (power-up)', 'Win (reach goal)', 'Sound (one-shot SFX)', 'Music (start / stop / crossfade)', 'None (marker)'],
                  default: 'hurt', label: 'Effect' },
                { key: 'w', kind: 'number', min: 1, max: 64, step: 1, default: 3, label: 'Width (cells)' },
                { key: 'h', kind: 'number', min: 1, max: 32, step: 1, default: 3, label: 'Height (cells)' },
                { key: 'visible', kind: 'choice',
                  options: ['hidden', 'visible'],
                  optionLabels: ['Hidden in game', 'Visible in game'],
                  default: 'hidden', label: 'In-Game Render' },
                // Audio-action props — shared between effect='sound'
                // (one-shot SFX on enter) and effect='music' (start /
                // stop / crossfade a long-running background track).
                // Both effects are edge-triggered: the verdict fires
                // once per ENTRY and the scene re-arms it on exit.
                { key: 'musicAction', kind: 'choice',
                  options: ['play', 'stop'],
                  optionLabels: ['Play a track (start or crossfade)', 'Stop the current music'],
                  default: 'play', label: 'Music Action',
                  hint: 'Play = start the chosen Audio Clip as the background music; if a track is already playing it crossfades into the new one (or hard-cuts when Fade = 0). Stop = fade EVERY active music source out (the background music AND any still-ringing Music Note tail) and leave silence (no Audio Clip needed).',
                  showIf: (pr) => pr?.effect === 'music' },
                { key: 'audio', kind: 'asset', assetKind: 'audio', default: '', label: 'Audio Clip',
                  hint: 'Import an MP3 / WAV / OGG into the Content Browser \u2192 Audio / Music, then pick it here.',
                  showIf: (pr) => pr?.effect === 'sound'
                      || (pr?.effect === 'music' && (pr?.musicAction || 'play') === 'play') },
                { key: 'volume', kind: 'number', min: 0, max: 1, step: 0.05, default: 1, label: 'Volume',
                  showIf: (pr) => pr?.effect === 'sound'
                      || (pr?.effect === 'music' && (pr?.musicAction || 'play') === 'play') },
                { key: 'loop', kind: 'choice',
                  options: ['yes', 'no'],
                  optionLabels: ['Loop forever', 'Play once'],
                  default: 'yes', label: 'Loop',
                  hint: 'Loop = the track restarts seamlessly until another zone stops or replaces it (background music). Play once = the track plays through then stops; another Play zone can start something else.',
                  showIf: (pr) => pr?.effect === 'music' && (pr?.musicAction || 'play') === 'play' },
                { key: 'musicFadeMs', kind: 'number', min: 0, max: 10000, step: 50, default: 500,
                  label: 'Fade (ms)',
                  hint: 'Crossfade length used when this zone fires. Play action: previous music fades OUT and new music fades IN over this many milliseconds (a true crossfade). Stop action: current music fades to silence over this duration. 0 = hard cut, 1000 = a smooth 1-second blend, 3000 = a slow cinematic fade.',
                  showIf: (pr) => pr?.effect === 'music' },
            ],
        },
        {
            // Camera zone — a scripted zoom region. `areaTool: true` opts
            // it into the rect-drag draw tool (drag a rectangle to size it).
            // While the player is inside, the camera eases to `zoom` and
            // either keeps following the player or locks to the zone centre.
            // Pure logic — no in-game render; `noBodyCollision` hides the
            // irrelevant per-body collision matrix from the inspector.
            type: 'camera.zone',
            label: PLATFORMER_ENTITY_DEFS['camera.zone'].label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS['camera.zone'].slot,
            collisionDefault: defaultCollisionFor('camera.zone'),
            noBodyCollision: true,
            areaTool: true,
            props: [
                { key: 'zoom', kind: 'number', min: 0.5, max: 3, step: 0.05, default: 1.6,
                  label: 'Zoom',
                  hint: 'Camera zoom while the player is inside this zone. 1 = normal, >1 zooms IN (closer — e.g. 1.6 for a tense corridor), <1 zooms OUT (wider — e.g. 0.7 to reveal a big room or boss). The camera eases in/out smoothly and snaps back when the player leaves.',
                  info: CAMERA_ZONE_INFO },
                { key: 'focus', kind: 'choice',
                  options: ['player', 'zone'],
                  optionLabels: ['Follow the player', 'Lock to zone centre'],
                  default: 'player', label: 'Focus',
                  hint: 'Follow the player = stay centred on the player at the new zoom (good for "zoom in on the action"). Lock to zone centre = frame this region itself, ignoring the player (good for a scripted reveal / cutscene shot).' },
                { key: 'w', kind: 'number', min: 1, max: 200, step: 1, default: 6, label: 'Width (cells)' },
                { key: 'h', kind: 'number', min: 1, max: 64, step: 1, default: 4, label: 'Height (cells)' },
            ],
        },
        {
            // Point light — a positional glow drawn into the runtime
            // lightmap. No artwork (slot null); the editor shows a bulb +
            // reach ring. `noBodyCollision` hides the irrelevant per-body
            // collision matrix from the inspector.
            type: 'light.point',
            label: PLATFORMER_ENTITY_DEFS['light.point'].label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS['light.point'].slot,
            collisionDefault: defaultCollisionFor('light.point'),
            noBodyCollision: true,
            props: [
                { key: 'color', kind: 'color', default: '#ffd9a0', label: 'Colour',
                  hint: 'Tint of the glow. Warm (#ffd9a0) for torches, blue for moonlight, red for lava.',
                  info: LIGHT_INFO },
                { key: 'radius', kind: 'number', min: 16, max: 1200, step: 4, default: 160, label: 'Radius (px)',
                  hint: 'How far the glow reaches in pixels. The reach ring in the editor shows it.' },
                { key: 'intensity', kind: 'number', min: 0, max: 4, step: 0.05, default: 1.1, label: 'Intensity',
                  hint: 'Brightness at the core. Overlapping lights add together (and can blow out to white).' },
                { key: 'flicker', kind: 'number', min: 0, max: 1, step: 0.05, default: 0, label: 'Flicker',
                  hint: '0 = steady. Higher = an organic wobble like a flame or a failing bulb.' },
            ],
        },
        {
            // Particle emitter (Phase 3). Purely visual — runs a live
            // ParticleSystem preview in the editor and emits in-game.
            type: 'fx.emitter',
            label: PLATFORMER_ENTITY_DEFS['fx.emitter'].label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS['fx.emitter'].slot,
            collisionDefault: defaultCollisionFor('fx.emitter'),
            // Purely visual: never engage the focus veil for emitters —
            // the dimming + amber haze distorts the live particle colours
            // the designer is trying to tune. See `_renderFocusOverlay`.
            noFocus: true,
            // The emitter is a bodiless spawn point, not a physical body,
            // so the generic Godot layer/mask "COLLISION" block is
            // meaningless here (its particles carry their own Collision /
            // Accumulate props instead). Skip it to avoid two confusingly
            // similar "Collision" sections in the inspector.
            noBodyCollision: true,
            // One-click starting points (Fire / Smoke / Clouds / Leaves /
            // Snow). The inspector renders these as a button row above the
            // properties; clicking applies the preset's look.
            presets: FX_EMITTER_PRESETS,
            // Group the (many) emitter props into meaningful inspector tabs
            // so the panel isn't one giant scroll. The inspector renders a
            // tab bar and shows only the active group's props, in `keys`
            // order. Every prop key must appear in exactly one tab.
            propTabs: [
                { id: 'emission', label: 'Emission',
                  keys: ['emit', 'triggerRadius', 'burst', 'rate', 'lifetime', 'spawnW', 'spawnH', 'maxParticles'] },
                { id: 'motion', label: 'Motion',
                  keys: ['speedMin', 'speedMax', 'angle', 'spread', 'gravity', 'radialAccel', 'tangentialAccel', 'damping', 'spin', 'turbulence', 'turbScale', 'turbSpeed'] },
                { id: 'look', label: 'Look',
                  keys: ['engine', 'startColor', 'endColor', 'palette', 'startSize', 'endSize', 'sizeRandom', 'startAlpha', 'endAlpha', 'texture', 'shape', 'blend'] },
                { id: 'collide', label: 'Collision',
                  keys: ['collide', 'bounce', 'accumulate', 'accumMax', 'accumWeight'] },
                { id: 'player', label: 'Player',
                  keys: ['playerInteract', 'interactStrength', 'interactRadius'] },
            ],
            props: [
                // Render engine: shaped/tinted particles vs. flat tumbling
                // confetti paper (the Frogger / Snake confetti look).
                { key: 'engine', kind: 'choice',
                  options: ['normal', 'confetti'],
                  optionLabels: ['Normal (shaped)', 'Confetti (paper)'],
                  default: 'normal', label: 'Engine',
                  hint: 'Normal = shaped, tinted particles (fire/smoke/magic). Confetti = flat tumbling paper in a colour palette, like the balloon + snake confetti.' },
                // Emission trigger — the "action" that drives particles.
                // Continuous = always on. Proximity = only while the
                // player is within the radius (leaves while passing a
                // tree). Contact = a one-shot burst each time the player
                // enters the radius (dust landing on a platform). Triggers
                // fire IN-GAME; the editor preview always emits so the
                // look stays tunable.
                { key: 'emit', kind: 'choice',
                  options: ['continuous', 'proximity', 'contact'],
                  optionLabels: ['Always on', 'When player is near', 'Burst on contact'],
                  default: 'continuous', label: 'Emit',
                  hint: 'Always on = constant stream. When player is near = emits only while the player is within the Trigger radius (e.g. leaves falling as the player passes a tree). Burst on contact = releases Burst particles each time the player enters the radius (e.g. dust when landing on a platform). Triggers only fire in-game; the editor preview always runs so you can tune it.' },
                { key: 'triggerRadius', kind: 'number', min: 16, max: 640, step: 8, default: 96,
                  label: 'Trigger radius (px)', hint: 'How close the player must get to fire the trigger.',
                  showIf: (pr) => (pr?.emit || 'continuous') !== 'continuous' },
                { key: 'burst', kind: 'number', min: 1, max: 300, step: 1, default: 16,
                  label: 'Burst count', hint: 'Particles released per contact trigger.',
                  showIf: (pr) => (pr?.emit || 'continuous') === 'contact' },
                { key: 'rate', kind: 'number', min: 0, max: 200, step: 1, default: 24, label: 'Rate (/sec)',
                  hint: 'Particles spawned per second. Independent of Lifetime — a continuous stream keeps about Rate × Lifetime particles alive at once.' },
                { key: 'lifetime', kind: 'number', min: 0.1, max: 60, step: 0.1, default: 1.2, label: 'Lifetime (sec)',
                  hint: 'How long each particle lives. Independent of Rate — raise it for long-lasting trails/snow; the emitter still spawns continuously (the pool auto-grows to fit).' },
                { key: 'speedMin', kind: 'number', min: 0, max: 400, step: 5, default: 20, label: 'Speed Min' },
                { key: 'speedMax', kind: 'number', min: 0, max: 400, step: 5, default: 60, label: 'Speed Max' },
                { key: 'angle', kind: 'number', min: 0, max: 360, step: 5, default: 0, label: 'Angle (0=up)' },
                { key: 'spread', kind: 'number', min: 0, max: 360, step: 5, default: 40, label: 'Spread (cone°)' },
                { key: 'gravity', kind: 'number', min: -400, max: 400, step: 5, default: 40, label: 'Gravity (px/s²)' },
                // Animated forces relative to the emitter centre.
                { key: 'radialAccel', kind: 'number', min: -600, max: 600, step: 10, default: 0,
                  label: 'Push (radial ±)', hint: 'Accel out from / into the centre. +blast, −implode.' },
                { key: 'tangentialAccel', kind: 'number', min: -600, max: 600, step: 10, default: 0,
                  label: 'Vortex (swirl ±)', hint: 'Sideways accel around the centre — makes particles spiral.' },
                { key: 'damping', kind: 'number', min: 0, max: 8, step: 0.1, default: 0,
                  label: 'Damping (drag)', hint: 'Slows particles over time so swirls/blasts settle.' },
                { key: 'spin', kind: 'number', min: 0, max: 720, step: 10, default: 0,
                  label: 'Spin (deg/s)', hint: 'Tumble rate for square/star particles.' },
                // Turbulent displace — an evolving "wind" flow field that
                // makes streams wander/churn organically (smoke eddies,
                // snow drift, embers). Off by default; the scale + speed
                // knobs only matter once strength is up.
                { key: 'turbulence', kind: 'number', min: 0, max: 600, step: 10, default: 0,
                  label: 'Turbulence', hint: 'Swirling wind-like displacement strength. 0 = off. Great for smoke, snow drift, leaves and embers.' },
                { key: 'turbScale', kind: 'number', min: 0.005, max: 0.2, step: 0.005, default: 0.02,
                  label: 'Turb. Scale', hint: 'Eddy size — lower = big lazy swirls, higher = small choppy turbulence.',
                  showIf: (pr) => (pr?.turbulence || 0) > 0 },
                { key: 'turbSpeed', kind: 'number', min: 0, max: 8, step: 0.1, default: 1,
                  label: 'Turb. Speed', hint: 'How fast the turbulence field churns over time.',
                  showIf: (pr) => (pr?.turbulence || 0) > 0 },
                // Collision (opt-in). Off = pure visual (no cost). Ground =
                // particles land on platforms / the ground; +player also
                // bounces them off the player. Works live in the editor
                // preview against painted solid tiles too.
                { key: 'collide', kind: 'choice',
                  options: ['off', 'ground', 'ground+player'],
                  optionLabels: ['Off (pass through)', 'Ground / walls', 'Ground + player'],
                  default: 'off', label: 'Collision',
                  hint: 'Off = particles pass through everything (cheapest). Ground = they land on solid tiles, platforms and the world floor. Ground + player also bounces them off the player.' },
                { key: 'bounce', kind: 'number', min: 0, max: 1, step: 0.05, default: 0.3,
                  label: 'Bounciness', hint: '0 = settle and pile up on impact, 1 = fully elastic bounce.',
                  showIf: (pr) => (pr?.collide || 'off') !== 'off' },
                // Accumulation: landed particles PARK as real resting flakes
                // and stack up (snow thickness) — they stay as actual
                // particles, live out their Lifetime, then die oldest-first
                // so the pile settles to a steady thickness. Implicitly
                // enables ground detection, so it works with Collision = Off.
                { key: 'accumulate', kind: 'choice',
                  options: ['off', 'on'],
                  optionLabels: ['Off', 'Pile up (build thickness)'],
                  default: 'off', label: 'Accumulate',
                  hint: 'When on, particles that land STAY as real resting flakes and stack on top of each other — snow piling up thickness, ash, sand. Each lives out its Lifetime then dies (oldest first), so the pile settles to a steady thickness. Tip: raise Lifetime + Max Particles for a deeper, longer-lasting pile.' },
                { key: 'accumMax', kind: 'number', min: 1, max: 400, step: 4, default: 64,
                  label: 'Max Thickness (px)', hint: 'A column stops accepting flakes once its pile reaches this height.',
                  showIf: (pr) => pr?.accumulate === 'on' },
                { key: 'accumWeight', kind: 'number', min: 0, max: 1, step: 0.05, default: 0.3,
                  label: 'Weight (squeeze)',
                  hint: 'How heavily flakes compress the ones beneath them. 0 = no squeeze (a loose, fluffy heap that just stacks). Higher = buried flakes pack tighter, so the pile becomes a denser mass and visibly sinks as more lands — like settling/melting snow. Combine with a long Lifetime + high Max Particles for a deep, slowly-compacting drift.',
                  showIf: (pr) => pr?.accumulate === 'on' },
                // Player interaction: how the live player reshapes nearby
                // particles. Replaces the old single "wake" knob with a
                // mode picker (off / slipstream / push / attract / repel /
                // swirl) + intensity + reach. Reads the legacy `wake` key
                // for back-compat. The (i) bubble opens the full guide.
                { key: 'playerInteract', kind: 'choice',
                  options: ['off', 'wake', 'push', 'attract', 'repel', 'swirl'],
                  optionLabels: [
                      'Off',
                      'Slipstream — drag along',
                      'Push aside — bow wave',
                      'Attract — pull in (magnet)',
                      'Repel — push away (bubble)',
                      'Swirl — orbit the player',
                  ],
                  default: 'off', label: 'Player Interaction',
                  hint: 'How the player reshapes nearby particles as it moves through them. Open the (i) for a full guide with examples.',
                  info: PLAYER_INTERACT_INFO },
                { key: 'interactStrength', kind: 'number', min: 0, max: 20, step: 0.5, default: 8,
                  label: 'Interaction Strength',
                  hint: 'How forcefully the player affects particles in range. Higher = snappier drag / harder shove / stronger pull. For Slipstream & Push it scales with how fast the player moves; Attract/Repel/Swirl work even when the player stands still.',
                  showIf: (pr) => (pr?.playerInteract || 'off') !== 'off' },
                { key: 'interactRadius', kind: 'number', min: 8, max: 512, step: 4, default: 80,
                  label: 'Interaction Range (px)',
                  hint: 'How far around the player the effect reaches. The pull/push is strongest right at the player and fades to nothing at the edge of this range.',
                  showIf: (pr) => (pr?.playerInteract || 'off') !== 'off' },
                // Colour model differs per engine: normal lerps a single
                // start→end colour; confetti picks per-piece from a palette.
                { key: 'startColor', kind: 'color', default: '#ffd24a', label: 'Start Colour',
                  showIf: (pr) => (pr?.engine || 'normal') !== 'confetti' },
                { key: 'endColor', kind: 'color', default: '#ff5a2a', label: 'End Colour',
                  showIf: (pr) => (pr?.engine || 'normal') !== 'confetti' },
                { key: 'palette', kind: 'palette',
                  default: ['#ef4444', '#f59e0b', '#fbbf24', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#ffffff'],
                  label: 'Confetti Palette',
                  hint: 'Each confetti piece picks a random colour from this set.',
                  showIf: (pr) => pr?.engine === 'confetti' },
                { key: 'startSize', kind: 'number', min: 1, max: 64, step: 1, default: 10, label: 'Start Size' },
                { key: 'endSize', kind: 'number', min: 0, max: 64, step: 1, default: 2, label: 'End Size' },
                { key: 'sizeRandom', kind: 'number', min: 0, max: 1, step: 0.05, default: 0,
                  label: 'Size Random', hint: 'Per-particle size jitter. 0 = every particle identical, 1 = ±100% — gives smoke / clouds / leaves a natural varied look.' },
                { key: 'startAlpha', kind: 'number', min: 0, max: 1, step: 0.05, default: 1, label: 'Start Alpha' },
                { key: 'endAlpha', kind: 'number', min: 0, max: 1, step: 0.05, default: 0, label: 'End Alpha' },
                // Use an imported PNG as the particle instead of a drawn
                // shape (leaf, spark, heart…). Picking one overrides Shape.
                { key: 'texture', kind: 'asset', default: '', label: 'PNG Particle',
                  hint: 'Use an imported image as the particle instead of a drawn shape — a leaf, a spark, a heart, etc. Pick any asset, or Import a new PNG via the Content Browser, then select it here. Leave on “Shape (no image)” to use the Shape below. Tip: bump Start/End Size for image particles.',
                  showIf: (pr) => (pr?.engine || 'normal') !== 'confetti' },
                // Shape + blend only apply to the normal engine; confetti
                // is always flat opaque paper, and a picked PNG overrides
                // the shape.
                { key: 'shape', kind: 'choice',
                  options: ['circle', 'square', 'star', 'spark', 'soft'],
                  optionLabels: ['Circle', 'Square', 'Star', 'Spark', 'Soft glow'],
                  default: 'circle', label: 'Shape',
                  showIf: (pr) => (pr?.engine || 'normal') !== 'confetti' && !pr?.texture },
                { key: 'blend', kind: 'choice',
                  options: ['add', 'normal'],
                  optionLabels: ['Additive (glow)', 'Normal'],
                  default: 'add', label: 'Blend',
                  showIf: (pr) => (pr?.engine || 'normal') !== 'confetti' },
                { key: 'spawnW', kind: 'number', min: 0, max: 512, step: 4, default: 0, label: 'Spawn Box W (px)' },
                { key: 'spawnH', kind: 'number', min: 0, max: 512, step: 4, default: 0, label: 'Spawn Box H (px)' },
                { key: 'maxParticles', kind: 'number', min: 1, max: 6000, step: 10, default: 120, label: 'Max Particles',
                  hint: 'Per-emitter ceiling on particles alive at once. It is auto-raised to at least Rate × Lifetime so a long lifetime never stalls the stream — it mainly matters for limiting big one-shot bursts. The global Particle Quality setting (Options) caps this further per device (Raspberry Pi → 2000 … high-end PC → 10000). To use fewer particles, lower Rate or Lifetime.' },
            ],
        },
        {
            // Music Note — Mario-Wonder style pass-through element that
            // plays a sound on contact. Several notes sharing one Audio
            // clip form a synced sequence: each plays a consecutive
            // 1-second slice in left-to-right order (manually
            // overridable with Slice Index).
            type: 'note',
            label: PLATFORMER_ENTITY_DEFS.note.label,
            positioning: 'tile-cell',
            allowedLayerIds: [PLATFORMER_LAYERS.entities],
            slot: PLATFORMER_ENTITY_DEFS.note.slot,
            collisionDefault: defaultCollisionFor('note'),
            // Sensor-only — no physical body, so hide the generic
            // collision matrix (the note responds to overlap, not
            // collide-and-block).
            noBodyCollision: true,
            props: [
                { key: 'audio', kind: 'asset', assetKind: 'audio', default: '', label: 'Audio',
                  hint: 'The imported MP3 / WAV / OGG to play. Import via Content Browser \u2192 Audio / Music, then pick it here.',
                  info: NOTE_INFO },
                { key: 'mode', kind: 'choice',
                  options: ['slice', 'oneShot'],
                  optionLabels: ['Slice \u2014 sync several notes', 'One-shot \u2014 play the whole clip'],
                  default: 'slice', label: 'Mode',
                  hint: 'Slice = each note in this stage sharing this clip plays one consecutive chunk of it (left-to-right order). One-shot = every contact plays the entire clip from the start.',
                  info: NOTE_INFO },
                { key: 'sliceDurationMs', kind: 'number', min: 50, max: 10000, step: 50, default: 1000,
                  label: 'Slice Duration (ms)',
                  hint: 'Length AND tempo of each chunk in milliseconds. In a row of Music Notes sharing the same Audio clip, slices are SCHEDULED back-to-back on the audio clock, so the tune always plays at this tempo no matter how fast the player runs through. 1000 = the song plays at its native pace (one note per second, Mario Wonder feel), 500 = double-time, 250 = quadruple-time, 2000 = half-speed. Walking slower than this leaves audible gaps between slices; walking faster just queues them up.',
                  showIf: (pr) => (pr?.mode || 'slice') === 'slice' },
                { key: 'sliceIndex', kind: 'number', min: -1, max: 64, step: 1, default: -1,
                  label: 'Slice Index (-1 = auto)',
                  hint: 'Leave at -1 to let the editor auto-assign this note\u2019s slot in left-to-right order. Set a number to lock this note to that specific slice (0 = first second).',
                  showIf: (pr) => (pr?.mode || 'slice') === 'slice' },
                { key: 'retrigger', kind: 'choice',
                  options: ['always', 'once'],
                  optionLabels: ['Always \u2014 replay on re-entry', 'Once \u2014 consume like a coin'],
                  default: 'always', label: 'Retrigger',
                  hint: 'Always = the player can re-run the tune by walking back. Once = the note disappears after the first contact.' },
                { key: 'tailMode', kind: 'choice',
                  options: ['auto', 'fade', 'cut'],
                  optionLabels: ['Auto \u2014 5 s fade on the last note',
                                 'Fade \u2014 custom fade-out length',
                                 'Cut \u2014 no fade, hard stop'],
                  default: 'auto', label: 'End of tune',
                  hint: 'Controls what happens after the LAST note in a row (the highest slice index) finishes its slice. Auto = 5 second held tail with a smooth fade-out so the tune ends on a decaying chord (Mario Wonder default). Fade = use the Fade-out (ms) value below to pick your own tail length. Cut = no extension, no fade \u2014 the slice ends, the music stops dead. Interior notes always play just their slice regardless of this setting.',
                  showIf: (pr) => (pr?.mode || 'slice') === 'slice' },
                { key: 'tailMs', kind: 'number', min: 0, max: 10000, step: 50, default: 1000,
                  label: 'Fade-out (ms)',
                  hint: 'How many milliseconds of extra audio from the same clip play past the LAST note\u2019s slice, with a smooth linear fade-out to silence. 0 = no fade (same as End of tune = Cut). 500 = a short half-second decay. 5000 = a long, cinematic five-second finale. Only used when End of tune is set to Fade.',
                  showIf: (pr) => (pr?.mode || 'slice') === 'slice'
                      && (pr?.tailMode || 'auto') === 'fade' },
                { key: 'volume', kind: 'number', min: 0, max: 1, step: 0.05, default: 1, label: 'Volume' },
            ],
        },
    ],

    /**
     * Renderer adapter — same shape as trainsim's. The platformer
     * board doubles as the in-game renderer (PlatformerScene reuses
     * it without `editorMode`) so the editor preview stays pixel-
     * identical to the live game.
     *
     * `paletteSvgFor` returns null because every tile in the palette
     * is `kind: 'sprite'` — the editor's palette code already handles
     * that path by resolving the SkinResolver slot's PNG, so no
     * inline-SVG fallback is needed.
     */
    renderer: {
        boardClass: PlatformerBoard,
        boardOptions: { editorMode: true },
        playScene: 'platformer',
        paletteSvgFor: () => null,
    },
};
